import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { sendPartnerNotification } from '@/utils/partnerNotifications';

export interface Achievement {
  id: string;
  user_id: string;
  baby_id: string | null;
  achievement_id: string;
  achievement_type: 'consistency' | 'exploration' | 'allergy_safety' | 'photo_enthusiast' | 'milestone';
  title: string;
  description: string | null;
  progress: number;
  max_progress: number | null;
  unlocked_at: string | null;
  seen: boolean;
  created_at: string;
}

// Achievement definitions
export const ACHIEVEMENT_DEFINITIONS = {
  // Consistency
  streak_7: { type: 'consistency' as const, title: '7-dagers streak', description: 'Logget mat 7 dager på rad', icon: 'flame' as const, maxProgress: 7 },
  streak_30: { type: 'consistency' as const, title: '30-dagers streak', description: 'Logget mat 30 dager på rad', icon: 'flame' as const, maxProgress: 30 },
  streak_100: { type: 'consistency' as const, title: '100-dagers streak', description: 'Logget mat 100 dager på rad', icon: 'flame' as const, maxProgress: 100 },
  
  // Exploration
  foods_10: { type: 'exploration' as const, title: 'Utforsker', description: '10 nye matvarer prøvd', icon: 'star' as const, maxProgress: 10 },
  foods_25: { type: 'exploration' as const, title: 'Mat-eventyrer', description: '25 nye matvarer prøvd', icon: 'star' as const, maxProgress: 25 },
  foods_50: { type: 'exploration' as const, title: 'Mat-mester', description: '50 nye matvarer prøvd', icon: 'trophy' as const, maxProgress: 50 },
  
  // Allergy Safety
  allergen_master: { type: 'allergy_safety' as const, title: 'Allergen-mester', description: 'Alle 8 store allergener introdusert trygt', icon: 'award' as const, maxProgress: 8 },
  patient_parent: { type: 'allergy_safety' as const, title: 'Tålmodig forelder', description: 'Fulgt allergen-protokoll 10 ganger', icon: 'target' as const, maxProgress: 10 },
  
  // Photo Enthusiast
  photographer: { type: 'photo_enthusiast' as const, title: 'Fotograf', description: '25 bilder tatt av måltider', icon: 'star' as const, maxProgress: 25 },
  memory_keeper: { type: 'photo_enthusiast' as const, title: 'Minnesamler', description: '100 bilder tatt av måltider', icon: 'trophy' as const, maxProgress: 100 },
  
  // Milestones
  first_food: { type: 'milestone' as const, title: 'Første smak', description: 'Logget første matvare', icon: 'star' as const, maxProgress: 1 },
  month_6_complete: { type: 'milestone' as const, title: 'Måned 6 fullført', description: 'Fullført en hel måned med logging', icon: 'award' as const, maxProgress: 1 },
};

export function useAchievements(babyId?: string) {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchAchievements();

    // Subscribe to changes
    const channel = supabase
      .channel(`achievement-changes-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_achievements',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchAchievements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, babyId]);

  const fetchAchievements = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (babyId) {
        query = query.eq('baby_id', babyId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setAchievements(data || []);
      setUnlockedAchievements(data?.filter(a => a.unlocked_at && !a.seen) || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkAndUnlockAchievement = async (
    achievementId: keyof typeof ACHIEVEMENT_DEFINITIONS,
    // Beholdt for bakoverkompatibilitet med eksisterende kall — verdien brukes ikke.
    // Serveren beregner faktisk progresjon selv fra brukerens data.
    _progress?: number
  ) => {
    if (!user) return null;

    const definition = ACHIEVEMENT_DEFINITIONS[achievementId];
    if (!definition) return null;

    try {
      // Serveren ignorerer klient-sendte verdier og beregner progresjon selv
      // (se award_achievement i databasen). Vi sender derfor kun nøytrale verdier.
      const { data, error } = await supabase.rpc('award_achievement', {
        _achievement_id: achievementId,
        _achievement_type: definition.type,
        _title: definition.title,
        _description: definition.description,
        _progress: 0,
        _max_progress: definition.maxProgress ?? 0,
        _baby_id: babyId ?? null,
      });

      if (error) throw error;

      const wasUnlocked = data?.unlocked_at != null;
      // Only treat as a "newly unlocked" event if it just unlocked AND user hasn't seen it
      const isFreshUnlock = wasUnlocked && data?.seen === false;

      if (isFreshUnlock) {
        // Avoid duplicate entries
        setUnlockedAchievements(prev =>
          prev.some(a => a.id === data.id) ? prev : [...prev, data as Achievement]
        );

        if (babyId) {
          try {
            await sendPartnerNotification({
              babyId,
              type: 'achievement',
              title: '🏆 Ny achievement!',
              body: `Partneren din unlocked "${definition.title}"`,
              metadata: {
                achievementId,
                achievementTitle: definition.title,
              },
            });
          } catch (notifError) {
            console.error('Error sending partner notification:', notifError);
          }
        }
        return data;
      }

      return null;
    } catch (error) {
      console.error('Error checking achievement:', error);
      return null;
    }
  };

  const markAchievementAsSeen = async (achievementId: string) => {
    try {
      await supabase
        .from('user_achievements')
        .update({ seen: true })
        .eq('id', achievementId);

      setUnlockedAchievements(prev => prev.filter(a => a.id !== achievementId));
    } catch (error) {
      console.error('Error marking achievement as seen:', error);
    }
  };

  return {
    achievements,
    unlockedAchievements,
    isLoading,
    checkAndUnlockAchievement,
    markAchievementAsSeen,
    refetch: fetchAchievements,
  };
}
